import type { ImageContent } from "@prismicio/types-internal/lib/content"
import type { Image } from "@prismicio/types-internal/lib/customtypes"
import { useState, useTransition } from "react"

import {
  getImageContentViews,
  getImageThumbnails,
  replaceImage,
} from "../../core/imageUtils"
import type { MediaAssetOrExternalImage } from "../../core/MediaLibrary/hooks/mediaLibraryData"
import type { CroppedImages } from "./CropperDialog"
import { updateImage } from "./CropperDialog/croppedImage"
import { useImageFieldImageUpload } from "./useImageFieldImageUpload"

interface useImageFieldProps {
  content: ImageContent | undefined
  field: Image
  onContentChange: (content: ImageContent | undefined) => void
}

export function useImageField(props: useImageFieldProps) {
  const { content, onContentChange, field } = props

  const [page, setPage] = useState(0)
  const [isUpdating, startTransition] = useTransition()

  const allThumbnails = getImageThumbnails(field)
  const thumbnail = allThumbnails[page]

  const allContentViews = content
    ? getImageContentViews(field, content)
    : undefined
  const contentView = allContentViews?.[page]

  function onMediaSelected(media: MediaAssetOrExternalImage) {
    if (!thumbnail) return

    const newContent = replaceImage(thumbnail.name, field, content, media)

    if (newContent !== content)
      startTransition(() => onContentChange(newContent))
  }

  function onPageChange(page: number) {
    startTransition(() => setPage(page))
  }

  function onCropperDialogClose({
    option,
    croppedImages,
  }: {
    option: number
    croppedImages: CroppedImages
  }) {
    setPage(option)

    if (!content) return

    const newContent = Object.entries(croppedImages).reduce(
      (prevContent, [thumbnail, croppedImage]) =>
        updateImage({
          field,
          content: prevContent,
          thumbnail,
          croppedImage,
        }),
      content,
    )

    if (newContent !== content)
      startTransition(() => onContentChange(newContent))
  }

  function onClear() {
    setPage?.(0)
    onContentChange(undefined)
  }

  const { uploadImage, isUploadingImage } = useImageFieldImageUpload({
    onMediaSelected,
  })

  return {
    onCropperDialogClose,
    onPageChange,
    onMediaSelected,
    isUpdating,
    page,
    thumbnail,
    allThumbnails,
    allContentViews,
    contentView,
    onClear,
    uploadImage,
    isUploadingImage,
  }
}
